/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ApiRef,
  errorApiRef,
  IconComponent,
  ProfileInfo,
  ProfileInfoApi,
  SessionApi,
  SessionState,
  useApi,
} from '@mainstage/core-plugin-api';
import {
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { WbButton, WbTooltip } from '@agilelab/plugin-wb-platform';
import { useEffect, useState } from 'react';
import { ProviderSettingsAvatar } from './ProviderSettingsAvatar';

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    alignItems: 'center',
    paddingInline: 0,
    flexWrap: 'nowrap',
    [theme.breakpoints.down(450)]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
    },
  },
  listItemText: {
    marginRight: theme.spacing(2),
    minWidth: 0,
    flex: 1,
    [theme.breakpoints.down(450)]: {
      marginRight: 0,
    },
  },
  secondaryTypography: {
    width: '100%',
  },
  profileWrapper: {
    width: '100%',
    flexShrink: 1,
  },
  profileGrid: {
    margin: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    minWidth: 0,
    gap: theme.spacing(2),
    [theme.breakpoints.down(450)]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  profileSummary: {
    width: '100%',
    minWidth: 0,
  },
  textWrap: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  identityContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionContainer: {
    marginLeft: 'auto',
    [theme.breakpoints.down(450)]: {
      marginLeft: 0,
      alignSelf: 'flex-start',
    },
  },
  actionButton: {
    whiteSpace: 'nowrap',
  },
}));

/** @public */
export const ProviderSettingsItem = (props: {
  title: string;
  description: string;
  icon: IconComponent;
  apiRef: ApiRef<ProfileInfoApi & SessionApi>;
}) => {
  const classes = useStyles();
  const { title, description, icon: Icon, apiRef } = props;

  const api = useApi(apiRef);
  const errorApi = useApi(errorApiRef);
  const [signedIn, setSignedIn] = useState(false);
  const emptyProfile: ProfileInfo = {};
  const [profile, setProfile] = useState(emptyProfile);

  useEffect(() => {
    let didCancel = false;

    const subscription = api
      .sessionState$()
      .subscribe((sessionState: SessionState) => {
        if (!didCancel) {
          api
            .getProfile({ optional: true })
            .then((profileResponse: ProfileInfo | undefined) => {
              if (!didCancel) {
                if (sessionState === SessionState.SignedIn) {
                  setSignedIn(true);
                }
                if (profileResponse) {
                  setProfile(profileResponse);
                }
              }
            });
        }
      });

    return () => {
      didCancel = true;
      subscription.unsubscribe();
    };
    // Added signedIn to dependency array to change the status after the logout/login
  }, [api, signedIn]);

  return (
    <ListItem className={classes.listItem}>
      <ListItemText
        className={classes.listItemText}
        secondary={
          <WbTooltip placement="top" arrow title={description}>
            <div className={classes.profileWrapper}>
              <div className={classes.profileGrid}>
                <div>
                  <Typography
                    style={{ textAlign: 'right', marginBottom: '0.5rem' }}
                    variant="body2"
                  >
                    {title}
                  </Typography>
                  <div className={classes.identityContainer}>
                    <Icon />
                    <ProviderSettingsAvatar
                      size={48}
                      picture={profile.picture}
                    />
                  </div>
                </div>
                <div className={classes.profileSummary}>
                  <Typography
                    className={classes.textWrap}
                    variant="subtitle1"
                    color="textPrimary"
                    gutterBottom
                    paragraph
                  >
                    {profile.displayName}
                  </Typography>
                  <Typography
                    className={classes.textWrap}
                    variant="body2"
                    color="textSecondary"
                  >
                    {profile.email}
                  </Typography>
                  <Typography
                    className={classes.textWrap}
                    variant="body2"
                    color="textSecondary"
                  >
                    {description}
                  </Typography>
                </div>
              </div>
            </div>
          </WbTooltip>
        }
        secondaryTypographyProps={{
          noWrap: false,
          className: classes.secondaryTypography,
        }}
      />
      <div className={classes.actionContainer}>
        <WbTooltip
          placement="top"
          arrow
          title={signedIn ? `Sign out from ${title}` : `Sign in to ${title}`}
        >
          <WbButton
            className={classes.actionButton}
            variant="outlined"
            color="primary"
            label={signedIn ? `Sign out` : `Sign in`}
            onClick={() => {
              const action = signedIn ? api.signOut() : api.signIn();
              action.then(() => setSignedIn(v => !v));
              action.catch(error => errorApi.post(error));
            }}
          />
        </WbTooltip>
      </div>
    </ListItem>
  );
};
